import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import {
  signInWithCustomToken,
  SIGNING_CUSTOMER_ID_KEY,
  SIGNING_SECTIONS_KEY,
} from '../../fire'
import { getApiBaseUrl } from '../../helpers/sendContractByEmail'

export default function SignContractPage() {
  const { token } = useParams<'token'>()
  const navigate = useNavigate()
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!token) {
      setError('Invalid signing link.')
      return
    }

    let cancelled = false

    async function init() {
      try {
        const res = await fetch(`${getApiBaseUrl()}/signing-session/${token}`)
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error((body as any).err || `Error ${res.status}`)
        }
        const { firebaseCustomToken, customerID, sections } = await res.json()
        if (cancelled) return
        await signInWithCustomToken(firebaseCustomToken)
        if (cancelled) return
        // Pins this browser session to only this customer's report — see
        // RequireAuth's guard in fire.tsx (V50/B77).
        sessionStorage.setItem(SIGNING_CUSTOMER_ID_KEY, customerID)
        if (Array.isArray(sections))
          sessionStorage.setItem(SIGNING_SECTIONS_KEY, JSON.stringify(sections))
        else sessionStorage.removeItem(SIGNING_SECTIONS_KEY)
        navigate(`/customers/${customerID}/report?mode=sign`, {
          replace: true,
        })
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    }

    init()
    return () => {
      cancelled = true
    }
  }, [token, navigate])

  if (error) {
    return (
      <div style={sx.container}>
        <p style={sx.title}>Unable to open contract</p>
        <p style={sx.message}>{error}</p>
      </div>
    )
  }

  return (
    <div style={sx.container}>
      <CircularProgress size={48} />
      <p style={sx.message}>Loading your contract...</p>
    </div>
  )
}

const sx = {
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
    height: '100vh',
    justifyContent: 'center',
  },
  message: { fontSize: 16, margin: 0 },
  title: { fontSize: 20, fontWeight: 'bold' as const, margin: 0 },
}
